import { useEffect, useState } from 'react';
import { api, type ApiError } from '../api/client';
import { Alert } from '../components/Field';
import { useAuth } from '../auth/AuthContext';

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  roles: string[];
  tenantName: string;
  lastLoginAt: string | null;
}

interface Page {
  items: UserRow[];
  page: number;
  totalItems: number;
  totalPages: number;
}

const PAGE_SIZE = 25;

export function Users() {
  const { user, can } = useAuth();
  const [data, setData] = useState<Page | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // '' is the signed-in administrator's own organization.
  const [tenantId, setTenantId] = useState('');
  const [organizations, setOrganizations] = useState<Array<{ id: string; name: string }>>([]);

  // The same permission the API checks before honouring tenantId, so the
  // picker never offers a choice the server would refuse.
  const crossTenant = can('CUSTOMER_CREATE') && can('CUSTOMER_VIEW');

  useEffect(() => {
    if (!crossTenant) return;
    api<{ items: Array<{ id: string; name: string }> }>('/admin/customers')
      .then((result) => setOrganizations(result.items))
      .catch(() => setOrganizations([]));
  }, [crossTenant]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (search) params.set('search', search);
      if (tenantId) params.set('tenantId', tenantId);
      const query = `?${params}`;
      api<Page>(`/admin/users${query}`)
        .then((result) => {
          if (!cancelled) {
            setData(result);
            setError(null);
          }
        })
        .catch((caught) => {
          if (!cancelled) setError(caught as ApiError);
        });
    }, 250); // debounce, so typing does not fire a request per keystroke

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, page, tenantId]);

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">All Learners &amp; Staff</h1>
        <p className="page__lede">
          Everyone who can sign in to SkillXzen, across every client company, with their role and
          status.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} /> : null}

      <div className="card">
        <div className="card__head">
          <span className="card__title">
            {data ? `${data.totalItems} user${data.totalItems === 1 ? '' : 's'}` : 'Loading…'}
          </span>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {crossTenant && organizations.length > 0 ? (
              <select
                className="cat__sort"
                aria-label="Company"
                value={tenantId}
                onChange={(event) => {
                  setTenantId(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">{user?.tenantName ?? 'My organization'}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              className="field__input"
              style={{ width: 260, maxWidth: '100%' }}
              placeholder="Search name or email"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {!data ? (
          <div className="spinner">Loading users…</div>
        ) : data.items.length === 0 ? (
          <div className="empty">No users match that search.</div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>
                      {user.firstName} {user.lastName}
                    </strong>
                  </td>
                  <td className="muted">{user.email}</td>
                  <td>{user.tenantName}</td>
                  <td>
                    {user.roles.map((role) => (
                      <span className="pill pill--role" key={role}>
                        {role}
                      </span>
                    ))}
                  </td>
                  <td>
                    <StatusPill status={user.status} />
                  </td>
                  <td className="muted">
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {data && data.totalPages > 1 ? (
          <div className="pager">
            <button
              className="iconbtn"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              ← Previous
            </button>
            <span className="muted">
              Page {data.page} of {data.totalPages} · {data.totalItems.toLocaleString()} users
            </span>
            <button
              className="iconbtn"
              disabled={page >= data.totalPages}
              onClick={() => setPage((value) => value + 1)}
            >
              Next →
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'ACTIVE' ? 'active' : status === 'PENDING_VERIFICATION' ? 'pending' : 'inactive';
  return <span className={`pill pill--${tone}`}>{status.replace(/_/g, ' ')}</span>;
}
